import { seesAllMunicipalityUnits } from "../../common/helpers/municipalityRoles";
import { ListOption } from "../../common/types";
import { AuthContextObject } from "../../contexts/AuthenticatedUserContext";
import { Unit } from "../UnitView/unitTypes";
import { Orderer, OrdererListingItem } from "./ordererTypes";

/**
 * Intersects authorised unit codes with available units as list options.
 */
export const listOptionsForAuthorisedUnits = (
    authorisedUnitCodes: string[] | undefined,
    allUnits: Unit[],
): ListOption[] =>
    allUnits
        .filter((unit) => authorisedUnitCodes?.includes(unit.code))
        .map((unit) => ({
            id: unit.code,
            name: unit.name,
        }));

/**
 * Filters the units that the orderer is authorised to
 * @param orderer  The orderer to filter units for
 * @param allUnits  All units available to the current user
 * @returns  The orderer and the units that the orderer is authorised to
 */
export const filterOrdererAuthorizedUnits = (
    orderer: Orderer,
    allUnits: Unit[],
) => listOptionsForAuthorisedUnits(orderer.authorisedUnitCodes, allUnits);

/**
 *  Filters the units that given orderers are authorised to
 * @param orderers  The orderers to filter units for
 * @param allUnits  All units available to the current user
 * @returns  The orderers and the units that the orderers are authorised to
 */
export const filterOrderersAuthorizedUnits = (
    orderers: Orderer[],
    allUnits: Unit[]
) =>
    orderers.reduce((array, orderer) => {
        const authUnits = filterOrdererAuthorizedUnits(orderer, allUnits);
        return [
            ...array,
            {
                orderer,
                authUnits,
            },
        ];
    }, [] as OrdererListingItem[]);

/**
 * Filters the units that the current user is authorised to
 * @param authUserData  The current user's auth data
 * @param units All units, nevermind the user's role
 * @returns The units that the current user is authorised to
 */
export const filterCurrentUserAuthorizedUnits = (
    authUserData: AuthContextObject,
    units: Unit[],
    municipalityId: string
) => {
    const authorizedAvailableUnits = units.filter((u) =>
        seesAllMunicipalityUnits(authUserData.municipalityRole)
            ? true
            : authUserData.municipalityAuthorisedUnitCodesMap[
                  municipalityId
              ]?.includes(u.code)
    );
    return authorizedAvailableUnits.map((unit) => ({
        id: unit.code,
        name: unit.name,
    }));
};

const textIncludesQuery = (value: string | undefined, query: string) =>
    (value ?? "").toLowerCase().includes(query);

const digitsOnly = (value: string) => value.replace(/\D/g, "");

const phoneIncludesQuery = (phone: string | undefined, query: string) => {
    if (textIncludesQuery(phone, query.toLowerCase())) {
        return true;
    }

    const queryDigits = digitsOnly(query);
    if (!queryDigits) {
        return false;
    }

    return digitsOnly(phone ?? "").includes(queryDigits);
};

/**
 * Filters listing items by a free-text search over name, email, phone, and unit names.
 * Empty or whitespace-only query returns all items.
 */
export const filterOrderersBySearchQuery = (
    items: OrdererListingItem[],
    query: string
) => {
    const trimmed = query.trim();
    if (!trimmed) {
        return items;
    }

    const normalizedQuery = trimmed.toLowerCase();

    return items.filter((item) => {
        const { firstName, lastName, municipalEmail, municipalPhoneNumber } =
            item.orderer;
        const fullName = [firstName, lastName].filter(Boolean).join(" ");

        if (
            textIncludesQuery(firstName, normalizedQuery) ||
            textIncludesQuery(lastName, normalizedQuery) ||
            textIncludesQuery(fullName, normalizedQuery) ||
            textIncludesQuery(municipalEmail, normalizedQuery) ||
            phoneIncludesQuery(municipalPhoneNumber, trimmed)
        ) {
            return true;
        }

        return item.authUnits.some((unit) =>
            textIncludesQuery(unit.name, normalizedQuery)
        );
    });
};

const LISTING_VISIBLE_UNIT_COUNT = 3;

/**
 * Formats authorised units for the orderer listing: first three names,
 * then +N when more remain.
 */
export const formatOrdererListingUnits = (units: ListOption[] | undefined) => {
    if (!units?.length) {
        return "Inga enheter";
    }

    const visibleNames = units
        .slice(0, LISTING_VISIBLE_UNIT_COUNT)
        .map((unit) => unit.name);
    const remaining = units.length - LISTING_VISIBLE_UNIT_COUNT;

    if (remaining > 0) {
        return `${visibleNames.join(", ")}, +${remaining}`;
    }

    return visibleNames.join(", ");
};

export const sortUnits = (data: ListOption[]) => {
    return data
        .map((item, i) => ({
            ...item,
            index: i,
        }))
        .sort((a, b) => {
            if (a.id < b.id) {
                return -1;
            }
            if (b.id < a.id) {
                return 1;
            }
            return 0;
        })
        .map((item) => data[item.index]);
};
